import {useRef} from 'react';
import {
    Alert,
    Button,
    Group,
    SimpleGrid,
    Stack,
    Text,
} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';
import {DataTableColumn} from 'mantine-datatable';

import {
    DataTableTokenPagination,
    FlexiDeleteButton,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useCertificates} from '../hooks/useCertificates';
import {useCertificatesSearch} from '../hooks/useCertificatesSearch';
import {useCertificatesSelection} from '../hooks/useCertificatesSelection';
import {useCertificatesFilter} from '../hooks/useCertificatesQuery';
import {useCertificatesTableColumns} from '../hooks/useCertificatesTableColumns';
import {useCertificateRequestConnections} from '../hooks/useCertificateRequestConnections';
import {useCertificateDeletion} from '../hooks/useCertificateDeletion';
import {DeleteCertificateModal} from '../components/DeleteCertificateModal';
import {ValidationRecordsPanel} from '../components/ValidationRecordsPanel';
import {Certificate} from '../types';

export const CertificatesTable = () => {

    const {columns} = useCertificatesTableColumns();

    const {
        items,
        visibleItems,
        attentionCount,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchCertificates,
        reload,
        isReloading,
    } = useCertificates();

    const {
        keyword,
        handleFilterByKeyword,
    } = useCertificatesSearch();

    const {
        filters,
        handleShowNeedsAttention,
    } = useCertificatesFilter();

    const {
        selectedRecords,
        setSelectedRecords,
    } = useCertificatesSelection();

    // ⚠️ THE DELETE CONTROL IS GATED ON THE SAME LIST THE REQUEST BUTTON USES,
    // AND SINCE FNP-1280 THAT IS AN APPROXIMATION RATHER THAN AN IDENTITY.
    //
    // It used to be exact: `main.ts` set `canRequest` and `canDelete` from one
    // `writable` flag, so the two questions had one answer. They are now two
    // declarations — `acm:RequestCertificate` and `acm:DeleteCertificate` are
    // separate statements in the tenant's role — and a tenant could in
    // principle hold one without the other.
    //
    // This still gates on the REQUEST list, deliberately, because the delete
    // control here is a bulk action over a selection that may span several
    // connections, and there is no single connection to ask about until rows
    // are selected. The approximation errs toward SHOWING the control to a
    // tenant who can request but not delete; that refusal is the provider's to
    // make, it comes back as a `deleted: false` result with its reason rather
    // than as an error, and `deleteCertificate` in the API refuses on
    // `canDelete` before any AWS call. Narrowing this to per-row capability is
    // a real improvement and a separate change.
    //
    // The mistake this DOES still prevent is the important one: a tenant with
    // no certificate-capable connection at all sees neither control.
    const {connections: writableConnections} = useCertificateRequestConnections();
    const canWrite = writableConnections.length > 0;

    const deletion = useCertificateDeletion();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const searching = keyword.trim() !== '';
    const filtering = Object.keys(filters).length > 0;

    // ⚠️ THE EMPTY STATES MEAN DIFFERENT THINGS and `noRecordsText` is one
    // string, so the distinction is branched here rather than flattened into a
    // single message that sends an operator hunting for a bug that is not
    // there. A FILTERED empty result is not "you have no certificates".
    let noRecordsText = I18n.get('No certificates yet');
    if (searching && filtering) {
        noRecordsText = I18n.get('No loaded certificate matches this search and filter');
    } else if (searching) {
        noRecordsText = I18n.get('No loaded certificate matches this search');
    } else if (filtering) {
        noRecordsText = filters.status === 'NEEDS_ATTENTION'
            // ⚠️ "NOTHING NEEDS ATTENTION" IS A FINDING, not an empty list. It
            // says every loaded certificate is either working or progressing
            // normally, which is the good news an operator came to check.
            ? I18n.get('Nothing loaded needs attention — every certificate is issued or still progressing normally.')
            : I18n.get('No loaded certificate matches this filter');
    }

    // ⚠️ ROW ACTIONS ARE A COLUMN THAT ONLY EXISTS WHEN WRITES ARE POSSIBLE.
    //
    // Appended rather than declared in useCertificatesTableColumns, because
    // whether it exists at all is a capability decision and the columns hook
    // has no business knowing about mutations.
    //
    // ⚠️ THERE IS NO EDIT ACTION, and that is the schema. A certificate is
    // immutable once issued — there is no `updateCertificate` mutation — so
    // delete is the only row action.
    const actionsColumn: DataTableColumn<Certificate> = {
        accessor: 'actions',
        title: I18n.get('Actions'),
        width: '120px',
        textAlign: 'right',
        render: (item: Certificate) => (
            <Group gap={'xs'} justify={'flex-end'} wrap={'nowrap'}>
                <FlexiDeleteButton
                    size={'compact-xs'}
                    variant={'subtle'}
                    onClick={() => deletion.requestDelete(item)}
                >
                    {I18n.get('Delete')}
                </FlexiDeleteButton>
            </Group>
        ),
    };

    const tableColumns = canWrite
        ? [...columns, actionsColumn]
        : columns;

    return (
        <SimpleGrid verticalSpacing={'xl'} m={'sm'}>

            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {/* ⚠️ DISTINCT FROM AN EMPTY TABLE: this is the QUERY failing, so
                nothing at all is known — not "you have no certificates". */}
            {isError && (
                <Alert color={'red'} title={I18n.get('Could not load certificates')}>
                    <Group justify={'space-between'} align={'center'}>
                        {I18n.get('The domains API did not return your certificates. This is not a provider problem — the request itself failed.')}
                        <Button
                            variant={'outline'}
                            size={'xs'}
                            onClick={reload}
                            loading={isReloading}
                        >
                            {I18n.get('Retry')}
                        </Button>
                    </Group>
                </Alert>
            )}

            {/* ⚠️ THE BANNER THAT MAKES A STUCK CERTIFICATE FINDABLE.

                The ticket's complaint is that a silent pending state lasting
                days is indistinguishable from a broken feature. An orange badge
                on row 40 of a long table is very nearly as silent, so the count
                is surfaced at the top with a way to filter straight to it. */}
            {!isError && attentionCount > 0 && filters.status !== 'NEEDS_ATTENTION' && (
                <Alert
                    color={'orange'}
                    variant={'light'}
                    title={I18n.get('Some certificates are not progressing')}
                >
                    <Group justify={'space-between'} align={'center'}>
                        <Text size={'sm'}>
                            {attentionCount === 1
                                ? I18n.get('One loaded certificate is stuck or had no validation record written. Neither resolves by waiting.')
                                : `${attentionCount} ${I18n.get('loaded certificates are stuck or had no validation record written. Neither resolves by waiting.')}`}
                        </Text>
                        <Button
                            variant={'outline'}
                            color={'orange'}
                            size={'xs'}
                            onClick={handleShowNeedsAttention}
                        >
                            {I18n.get('Show them')}
                        </Button>
                    </Group>
                </Alert>
            )}

            <MantineDataTable<Certificate>
                columns={tableColumns}
                records={visibleItems}
                idAccessor={'id'}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                // Client-side sorting over the pages loaded so far — the same
                // scope the keyword and the filters work on. Token pagination
                // cannot reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                // ⚠️ THE LOADING STATE, distinct from empty and from error.
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchCertificates()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                // ⚠️ THE VALIDATION RECORDS EXPAND IN PLACE, and only where
                // they are the answer to a question.
                //
                // A certificate that is stuck or was never written is one an
                // operator has to act on, and the first thing they need is the
                // record to compare against the zone. Making them navigate away
                // to find it is the hand-copying friction this module exists to
                // remove, one level up.
                rowExpansion={{
                    trigger: 'click',
                    allowMultiple: true,
                    content: ({record}) => (
                        <Stack p={'md'} gap={'sm'}>
                            <ValidationRecordsPanel certificate={record}/>
                        </Stack>
                    ),
                }}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                // Passing a term is what selects the filtered footer state —
                // omit it and a narrowed view silently reports the unfiltered
                // one.
                filterTerm={searching ? keyword : (filtering ? 'filtered' : undefined)}
                noun={'certificate'}
                nextToken={nextToken}
                isFetchingMore={isFetchingNextPage}
                handleLoadMore={fetchCertificates}
            />

            <DeleteCertificateModal
                certificate={deletion.certificate}
                opened={deletion.opened}
                onClose={deletion.cancelDelete}
                onConfirm={deletion.confirmDelete}
                isDeleting={deletion.isDeleting}
                refusal={deletion.refusal}
                error={deletion.error}
            />

        </SimpleGrid>
    );
};
